<script lang="ts">
  import { onMount } from 'svelte';
  import Hls from 'hls.js';

  export let src: string;
  export let poster: string | undefined = undefined;
  export let autoplay: boolean = false;
  export let muted: boolean = false;
  export let loop: boolean = false;
  export let controls: boolean = true;
  export let className: string = '';

  let videoElement: HTMLVideoElement;
  let hls: Hls | null = null;
  let isLoading = true;
  let error: string | null = null;
  let retryCount = 0;
  const MAX_RETRIES = 3;

  async function initializeVideo() {
    if (!videoElement) return;
    
    isLoading = true;
    error = null;

    try {
      // Check if the video source is accessible
      const response = await fetch(src, { method: 'HEAD' });
      if (!response.ok) {
        throw new Error('Video source not accessible');
      }

      if (Hls.isSupported()) {
        if (hls) {
          hls.destroy();
        }

        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          maxBufferLength: 30,
          maxMaxBufferLength: 600,
          maxBufferSize: 60 * 1000 * 1000,
          maxBufferHole: 0.5,
          abrEwmaDefaultEstimate: 500000,
          abrMaxWithRealBitrate: true,
          startLevel: -1,
          testBandwidth: true,
          progressive: true,
          debug: false
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                error = "Network error. Please check your connection.";
                retryLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                error = "Media error. The video might be corrupted.";
                retryLoad();
                break;
              default:
                error = "An error occurred while loading the video.";
                break;
            }
            isLoading = false;
          }
        });

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          isLoading = false;
          if (autoplay) {
            videoElement.play().catch(error => {
              console.warn("Autoplay prevented:", error);
            });
          }
        });

        hls.loadSource(src);
        hls.attachMedia(videoElement);
      } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
        // For Safari with native HLS support
        videoElement.src = src;
        isLoading = false;
        if (autoplay) {
          videoElement.play().catch(error => {
            console.warn("Autoplay prevented:", error);
          });
        }
      } else {
        // Fallback for browsers that don't support HLS
        const fallbackSrc = src.replace('playlist.m3u8', 'video.mp4');
        videoElement.src = fallbackSrc;
        isLoading = false;
      }
    } catch (err) {
      console.error('Video initialization error:', err);
      error = "Failed to load video. Please try again later.";
      isLoading = false;
    }
  }

  function retryLoad() {
    if (retryCount < MAX_RETRIES) {
      retryCount++;
      setTimeout(() => {
        initializeVideo();
      }, 1000 * retryCount); // Exponential backoff
    }
  }

  onMount(() => {
    initializeVideo();
    return () => {
      if (hls) {
        hls.destroy();
        hls = null;
      }
    };
  });
</script>

<div class="relative w-full h-full {className}">
  {#if isLoading}
    <div class="absolute inset-0 flex items-center justify-center bg-gray-100">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
    </div>
  {:else if error}
    <div class="absolute inset-0 flex items-center justify-center bg-gray-100">
      <div class="text-center p-4">
        <p class="text-red-500 mb-4">{error}</p>
        <button 
          class="px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800 transition-colors"
          on:click={() => {
            retryCount = 0;
            initializeVideo();
          }}
        >
          Retry
        </button>
      </div>
    </div>
  {/if}

  <video
    bind:this={videoElement}
    {poster}
    {autoplay}
    {muted}
    {loop}
    {controls}
    class="w-full h-full object-contain"
    playsinline
    on:error={() => {
      error = "Failed to load video. Please try again later.";
      isLoading = false;
    }}
  >
    <track kind="captions" />
  </video>
</div>

<style>
  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
</style> 